import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { MapPin, RefreshCw, CheckCircle2, Building2, User, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../config";
import { dealerApi } from "../../utils/dealerApi";
import { getLocalizedCountryName } from "../../constants/countries";

const COUNTRY_FLAGS = {
  IT: "🇮🇹", FR: "🇫🇷", ES: "🇪🇸", DE: "🇩🇪", UK: "🇬🇧", PT: "🇵🇹",
  NL: "🇳🇱", BE: "🇧🇪", PL: "🇵🇱", SE: "🇸🇪", NO: "🇳🇴", FI: "🇫🇮",
  DK: "🇩🇰", CZ: "🇨🇿", GR: "🇬🇷", CH: "🇨🇭", GBZ: "🇬🇮", IRL: "🇮🇪",
};
const flag = (c) => COUNTRY_FLAGS[c] || "🏳";
// Country name localised to the dealer's UI language (Fabrizio CR 23/02/2026
// i18n leak — was hardcoded Italian regardless of dealer's UI lang).
const _dealerLang = () => (typeof window !== "undefined" ? localStorage.getItem("dealer_lang") : null) || "UK";
const name = (c) => getLocalizedCountryName(c, _dealerLang());

/**
 * Dealer-scoped Mismatch widget.
 * Shows ONLY the dealer's (or seller's) own customers whose country looks
 * inconsistent with the VAT/phone prefix. One-click correction inline.
 *
 * `t` is the translation dictionary inherited from DealerPortal — see
 * DealerDashboardView for the call site. Defensive default to {} so a
 * caller that forgets to pass `t` never blows up React with a
 * `ReferenceError: t is not defined` (this caused a production black
 * page on 1/6/2026 — the widget always rendered before the bug
 * surfaced because preview test dealers had zero customers and
 * returned null at the top guard, hiding the issue from staging).
 */
export const DealerMismatchWidget = ({ dealerToken, t = {} }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    if (!dealerToken) return;
    try {
      setLoading(true);
      const r = await dealerApi.get(`/dealer/mismatch-detection`, {
        token: dealerToken,
        config: { params: { days: 30 } },
      });
      setData(r.data);
    } catch {
      // silent — dealer portal already shows its own errors elsewhere
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dealerToken]);

  const applyFix = async (cust) => {
    try {
      setFixing(cust.id);
      await dealerApi.post(`/customers/${cust.id}/fix-country`, { token: dealerToken }, {
        new_country: cust.suggested_country,
        reason: reason || (t.mismatchCorrectionReasonDefault || "Corretto dal widget Mismatch (dealer)"),
      });
      toast.success(`${t.mismatchCountryUpdated || "Paese aggiornato:"} ${flag(cust.suggested_country)} ${name(cust.suggested_country)}`);
      setConfirmId(null); setReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.mismatchCorrectionFailed || "Errore correzione");
    } finally {
      setFixing(null);
    }
  };

  // Don't render anything while loading first time or if no data
  if (loading && !data) return null;
  if (!data) return null;

  // Zero-state: small clean card so the dealer sees positive feedback too
  if (data.mismatched_count === 0) {
    if (data.total_in_window === 0) return null;  // brand new account — nothing to brag about yet
    return (
      <div className="wallet-card mt-4 flex items-center gap-3 bg-lojack-blue-500/5 border-lojack-blue-500/20" data-testid="dealer-mismatch-clean">
        <CheckCircle2 className="text-lojack-blue-400 flex-shrink-0" size={20} />
        <div className="flex-1">
          <p className="text-lojack-blue-300 text-sm font-medium">{t.mismatchCleanTitle || "Qualità dati paese: ✅ Ottimo lavoro"}</p>
          <p className="text-gray-500 text-xs">{(t.mismatchCleanBody || "Tutti i {n} clienti degli ultimi 30 giorni hanno il paese coerente con P.IVA/telefono.").replace('{n}', data.total_in_window)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wallet-card mt-4 border-lojack-clause-500/30 bg-lojack-clause-950/10" data-testid="dealer-mismatch-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="text-lojack-clause-400" size={18} />
          <h3 className="text-white text-sm font-semibold">{t.mismatchTitle || "Clienti con paese da rivedere"}</h3>
          <span className="bg-lojack-clause-500/20 text-lojack-clause-300 px-2 py-0.5 rounded-full text-[10px] font-bold">
            {data.mismatched_count}
          </span>
        </div>
        <button onClick={load} className="text-gray-500 hover:text-white transition" data-testid="dealer-mismatch-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>
      <p className="text-gray-400 text-xs mb-3">
        {t.mismatchBodyIntro || "Negli ultimi 30 giorni hai creato"} <span className="text-white font-medium">{data.total_in_window}</span> {t.mismatchBodyMiddle || "clienti, di cui"}{' '}
        <span className="text-lojack-clause-300 font-medium">{data.mismatched_count}</span> {t.mismatchBodyEnd || "hanno un paese diverso da quello suggerito da P.IVA o prefisso telefonico. Correggi con un click."}
      </p>

      <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
        {data.mismatched_customers.map(c => (
          <div key={c.id} className="bg-black/40 rounded-lg border border-gray-800 p-3" data-testid={`dealer-mismatch-row-${c.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {c.customer_type === 'business'
                    ? <Building2 size={12} className="text-purple-400 flex-shrink-0" />
                    : <User size={12} className="text-blue-400 flex-shrink-0" />}
                  <span className="text-white text-sm font-medium truncate">{c.display_name}</span>
                </div>
                <div className="text-xs text-gray-500 truncate">{c.email}</div>
                <div className="text-xs text-gray-400 mt-1.5">
                  <span className="text-gray-500">{t.mismatchCurrent || "Attuale:"}</span> {flag(c.current_country)} {name(c.current_country)}
                  <span className="mx-2 text-gray-600">→</span>
                  <span className="text-lojack-clause-400 font-medium">{flag(c.suggested_country)} {name(c.suggested_country)}</span>
                  <span className="ml-2 text-[10px] text-gray-500 uppercase">({c.source === 'vat' ? (t.mismatchSourceVat || 'da P.IVA') : (t.mismatchSourcePhone || 'da telefono')})</span>
                </div>
              </div>
              <button
                onClick={() => { setConfirmId(c.id); setReason(""); }}
                disabled={fixing === c.id}
                className="px-3 py-1.5 bg-lojack-clause-600 hover:bg-lojack-clause-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white text-xs font-medium transition flex-shrink-0"
                data-testid={`dealer-mismatch-fix-${c.id}`}>
                {t.mismatchFixBtn || "Correggi"}
              </button>
            </div>

            {confirmId === c.id && (
              <div className="mt-3 pt-3 border-t border-gray-800 space-y-2" data-testid={`dealer-mismatch-confirm-${c.id}`}>
                <p className="text-xs text-gray-300">
                  {t.mismatchConfirmQuestion || "Conferma cambio paese a"} <span className="text-lojack-clause-400 font-medium">{flag(c.suggested_country)} {name(c.suggested_country)}</span>?
                </p>
                <input
                  type="text"
                  placeholder={t.mismatchReasonPlaceholder || "Motivo correzione (opzionale)"}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full bg-black border border-gray-700 rounded px-2 py-1 text-xs text-white placeholder-gray-600 focus:border-lojack-clause-500 outline-none"
                  data-testid={`dealer-mismatch-reason-${c.id}`}
                  maxLength={500}
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => applyFix(c)}
                    disabled={fixing === c.id}
                    className="flex-1 px-3 py-1.5 bg-lojack-blue-600 hover:bg-lojack-blue-700 disabled:opacity-50 rounded-lg text-white text-xs font-medium"
                    data-testid={`dealer-mismatch-confirm-apply-${c.id}`}>
                    {fixing === c.id ? (t.mismatchSaving || "Salvataggio...") : (t.mismatchConfirmFix || "Conferma correzione")}
                  </button>
                  <button
                    onClick={() => { setConfirmId(null); setReason(""); }}
                    className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-xs">
                    {t.mismatchCancel || "Annulla"}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DealerMismatchWidget;

// PropTypes — defensive validation so future refactors that forget
// to pass `t` (the bug that caused the 1/6/2026 black-page incident)
// surface as a console warning in dev BEFORE reaching production.
// `t` has a runtime default of `{}` in the destructure so a missing
// dictionary never throws — PropTypes still warns the developer.
DealerMismatchWidget.propTypes = {
  dealerToken: PropTypes.string.isRequired,
  t: PropTypes.object,
};
